import { useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import {
  logRelicCameraPresets,
  readRelicCameraPreset,
  writeRelicCameraPreset,
} from "../utils/questRelicCameraPreferences";
import loadingHourglass from "../assets/loading.json";
import "./QuestRelicFbxViewer.css";

const TARGET_MODEL_SIZE = 3.6;
const DEFAULT_CAMERA_FIT_OFFSET = 1.42;
const DRACO_DECODER_PATH = `${process.env.PUBLIC_URL || ""}/draco/gltf/`;

let sharedGltfLoader = null;

/** Chargeur unique : les reliques sont des GLB compressés Draco. */
function getRelicGltfLoader() {
  if (sharedGltfLoader) return sharedGltfLoader;

  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
  dracoLoader.preload();

  const loader = new GLTFLoader();
  loader.setCrossOrigin("anonymous");
  loader.setDRACOLoader(dracoLoader);
  sharedGltfLoader = loader;
  return loader;
}

function loadRelicObject(modelUrl, onLoad, onError) {
  getRelicGltfLoader().load(modelUrl, (gltf) => onLoad(gltf.scene), undefined, onError);
}

/** Plans de studio laissés dans l'export Max (ex. « Wall ») — pas la relique. */
function isStudioHelperMesh(mesh) {
  if (!mesh.isMesh) return false;

  const name = (mesh.name || "").toLowerCase();
  if (name === "wall" || name.includes("wall")) return true;

  mesh.geometry?.computeBoundingBox();
  const box = mesh.geometry?.boundingBox;
  if (!box) return false;

  const size = box.getSize(new THREE.Vector3());
  const dims = [size.x, size.y, size.z].sort((a, b) => b - a);
  const isFlatPlane = dims[2] / Math.max(dims[0], Number.EPSILON) < 0.05;
  const isHuge = dims[0] > 500;

  return isFlatPlane && isHuge;
}

function hideStudioHelpers(object) {
  object.traverse((child) => {
    if (isStudioHelperMesh(child)) {
      child.visible = false;
    }
  });
}

/**
 * Certains exports FBX (ex. Reliques Africaines) posent transparent + alphaMap
 * et une teinte grise sur des meshes solides → rendu pâle / fantôme.
 */
function prepareRelicMaterials(object) {
  object.traverse((child) => {
    if (!child.isMesh || !child.material) return;

    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];

    materials.forEach((material) => {
      if (!material) return;

      const wasTransparent = Boolean(material.transparent);

      if (material.alphaMap) {
        material.alphaMap = null;
      }

      if (typeof material.opacity !== "number" || material.opacity >= 0.99) {
        material.opacity = 1;
        material.transparent = false;
        material.depthWrite = true;
      }

      // Évite le voile gris (#ccc) multiplié sur la texture.
      if (material.map && material.color) {
        material.color.setRGB(1, 1, 1);
      }

      // Sur les exports « ghost » (transparent), l'émissive blanche laveur le mesh.
      if (wasTransparent && material.emissive) {
        material.emissive.setRGB(0, 0, 0);
      }

      material.needsUpdate = true;
    });
  });
}

function computeVisibleMeshBounds(object) {
  const box = new THREE.Box3();
  let found = false;

  object.updateMatrixWorld(true);
  object.traverse((child) => {
    if (!child.isMesh || !child.visible) return;
    if (!child.geometry?.attributes?.position?.count) return;

    const geometry = child.geometry;
    if (!geometry.boundingBox) {
      geometry.computeBoundingBox();
    }
    if (!geometry.boundingBox) return;

    const meshBox = geometry.boundingBox.clone();
    meshBox.applyMatrix4(child.matrixWorld);
    box.union(meshBox);
    found = true;
  });

  return found && !box.isEmpty() ? box : null;
}

function fitCameraToBounds(box, camera, controls, fitOffset = DEFAULT_CAMERA_FIT_OFFSET) {
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z, 0.001);
  const fovRadians = (camera.fov * Math.PI) / 180;
  const distance = (maxDim / 2 / Math.tan(fovRadians / 2)) * fitOffset;

  camera.near = Math.max(distance / 500, 0.01);
  camera.far = Math.max(distance * 500, 100);
  camera.position.set(distance * 0.72, distance * 0.48, distance * 0.92);
  camera.lookAt(0, 0, 0);
  camera.updateProjectionMatrix();

  controls.target.set(0, 0, 0);
  controls.minDistance = distance * 0.35;
  controls.maxDistance = distance * 3.2;
  controls.update();

  return {
    fitOffset,
    cameraDistance: camera.position.distanceTo(controls.target),
  };
}

function resolveInitialFitOffset(relicId, cameraFitOffset) {
  if (Number.isFinite(cameraFitOffset) && cameraFitOffset > 0) {
    return cameraFitOffset;
  }

  const savedPreset = readRelicCameraPreset(relicId);
  if (savedPreset?.fitOffset) {
    return savedPreset.fitOffset;
  }

  return DEFAULT_CAMERA_FIT_OFFSET;
}

export default function QuestRelicFbxViewer({
  modelUrl,
  label = "Relique",
  relicId = null,
  cameraFitOffset = null,
  modelRotation = null,
  displayVariant = "default",
  showCaption = true,
  lightingProfile = "default",
}) {
  const mountRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const isShowcase = displayVariant === "showcase";
  const useContrastLighting = lightingProfile === "contrast";

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !modelUrl) return undefined;

    const scene = new THREE.Scene();
    if (isShowcase) {
      scene.background = null;
    } else {
      scene.background = new THREE.Color(0xf7efe4);
    }

    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 5000);
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: isShowcase,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.className = "quest-relic-viewer__canvas";
    mount.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.65;

    if (isShowcase && useContrastLighting) {
      // Matériaux mats (bois, cuir…) : moins d'ambiant, plus de directionnel.
      scene.add(new THREE.HemisphereLight(0xfff1df, 0x2a1834, 0.72));
      scene.add(new THREE.AmbientLight(0xffffff, 0.18));

      const keyLight = new THREE.DirectionalLight(0xfff8ef, 2.15);
      keyLight.position.set(5, 8, 6);
      scene.add(keyLight);

      const fillLight = new THREE.DirectionalLight(0xffe2b8, 0.55);
      fillLight.position.set(-3, 4, 2);
      scene.add(fillLight);

      const rimLight = new THREE.DirectionalLight(0xffcf8a, 1.05);
      rimLight.position.set(-4, 2, -5);
      scene.add(rimLight);
    } else if (isShowcase) {
      scene.add(new THREE.HemisphereLight(0xfff1df, 0x3a2448, 1.25));
      scene.add(new THREE.AmbientLight(0xffffff, 0.42));

      const keyLight = new THREE.DirectionalLight(0xfff8ef, 1.35);
      keyLight.position.set(5, 8, 6);
      scene.add(keyLight);

      const rimLight = new THREE.DirectionalLight(0xffcf8a, 0.62);
      rimLight.position.set(-4, 2, -5);
      scene.add(rimLight);
    } else {
      scene.add(new THREE.HemisphereLight(0xfff8ef, 0x8b7355, 1.1));
      scene.add(new THREE.AmbientLight(0xffffff, 0.45));

      const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
      keyLight.position.set(5, 8, 6);
      scene.add(keyLight);
    }

    let modelRoot = null;
    let animationFrameId = 0;
    let disposed = false;
    let referenceFitOffset = resolveInitialFitOffset(relicId, cameraFitOffset);
    let referenceCameraDistance = 0;

    const persistCameraPreset = () => {
      if (!relicId || !referenceCameraDistance) return;

      const currentDistance = camera.position.distanceTo(controls.target);
      const nextFitOffset = referenceFitOffset * (currentDistance / referenceCameraDistance);

      writeRelicCameraPreset(relicId, { fitOffset: nextFitOffset });
      logRelicCameraPresets(relicId, nextFitOffset);

      referenceFitOffset = nextFitOffset;
      referenceCameraDistance = currentDistance;
    };

    const handleControlsEnd = () => {
      persistCameraPreset();
    };

    controls.addEventListener("end", handleControlsEnd);

    const resize = () => {
      const width = Math.max(mount.clientWidth, 1);
      const height = Math.max(mount.clientHeight, 1);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);
    resize();

    loadRelicObject(
      modelUrl,
      (object) => {
        if (disposed) return;

        hideStudioHelpers(object);
        prepareRelicMaterials(object);

        if (Array.isArray(modelRotation)) {
          const [rotX = 0, rotY = 0, rotZ = 0] = modelRotation;
          object.rotation.set(
            THREE.MathUtils.degToRad(rotX),
            THREE.MathUtils.degToRad(rotY),
            THREE.MathUtils.degToRad(rotZ)
          );
        }

        modelRoot = new THREE.Group();
        modelRoot.add(object);
        object.updateMatrixWorld(true);

        const initialBox = computeVisibleMeshBounds(object);
        if (!initialBox) {
          setStatus("error");
          setErrorMessage("Le modèle 3D ne contient pas de géométrie visible.");
          return;
        }

        const center = initialBox.getCenter(new THREE.Vector3());
        object.position.sub(center);
        object.updateMatrixWorld(true);

        const centeredBox = computeVisibleMeshBounds(object);
        if (!centeredBox) {
          setStatus("error");
          setErrorMessage("Impossible de cadrer le modèle 3D.");
          return;
        }

        const centeredSize = centeredBox.getSize(new THREE.Vector3());
        const maxDim = Math.max(centeredSize.x, centeredSize.y, centeredSize.z, Number.EPSILON);
        modelRoot.scale.setScalar(TARGET_MODEL_SIZE / maxDim);
        modelRoot.updateMatrixWorld(true);

        const fittedBox = computeVisibleMeshBounds(modelRoot);
        if (!fittedBox) {
          setStatus("error");
          setErrorMessage("Impossible de cadrer le modèle 3D.");
          return;
        }

        const cameraPreset = fitCameraToBounds(
          fittedBox,
          camera,
          controls,
          referenceFitOffset
        );
        referenceFitOffset = cameraPreset.fitOffset;
        referenceCameraDistance = cameraPreset.cameraDistance;

        scene.add(modelRoot);
        setStatus("ready");
      },
      (error) => {
        if (disposed) return;
        setStatus("error");
        setErrorMessage(error?.message || "Impossible de charger le modèle 3D.");
      }
    );

    const animate = () => {
      animationFrameId = window.requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      disposed = true;
      controls.removeEventListener("end", handleControlsEnd);
      window.cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      controls.dispose();

      if (modelRoot) {
        modelRoot.traverse((child) => {
          if (child.isMesh) {
            child.geometry?.dispose();
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => material.dispose());
            } else {
              child.material?.dispose();
            }
          }
        });
        scene.remove(modelRoot);
      }

      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [modelUrl, relicId, cameraFitOffset, modelRotation, isShowcase, useContrastLighting]);

  return (
    <div
      className={[
        "quest-relic-viewer",
        isShowcase && "quest-relic-viewer--showcase",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={label}
    >
      <div ref={mountRef} className="quest-relic-viewer__stage" />
      {status === "loading" ? (
        <div
          className="quest-relic-viewer__loading"
          role="status"
          aria-label="Chargement de la relique"
        >
          <Lottie animationData={loadingHourglass} loop autoplay />
        </div>
      ) : null}
      {status === "error" ? (
        <p className="quest-relic-viewer__status quest-relic-viewer__status--error" role="alert">
          {errorMessage}
        </p>
      ) : null}
      {showCaption ? <p className="quest-relic-viewer__caption">{label}</p> : null}
    </div>
  );
}
